/**
 * @file TextInput atom — low-level single-line text entry (RFC §3).
 * @description Wraps the react-native TextInput primitive directly (no
 * Gluestack chrome), for bare inputs that the compound `Input` atom would
 * over-decorate. Callers pass data (value, onChangeText, placeholder,
 * keyboardType); the public `style` prop is replaced by the counted
 * `UNSAFE_style` escape (it speaks the banned dialect), and testID +
 * accessibility props forward through — test/a11y identity is part of the
 * atom API (RFC standing constraint 4).
 * @module components/atoms/TextInput
 */
import React from "react";

import { TextInput as RNTextInput } from "react-native";

import { withTextVariant } from "../types";

import type { TextVariant, WithUnsafeStyle } from "../types";

/**
 *
 */
type RNTextInputProps = React.ComponentProps<typeof RNTextInput>;

/**
 * Public TextInput atom props — the RN primitive's props minus the banned
 * public `style` (use `UNSAFE_style` instead).
 */
export interface TextInputProps
  extends
    Omit<RNTextInputProps, "style">,
    WithUnsafeStyle<RNTextInputProps["style"]> {
  /**
   * Closed text-style enum, sharing the `Text`/`Heading` vocabulary
   * (`TextVariant`). The sanctioned way to set this field's typography —
   * it replaces raw size/weight utilities in `className`.
   *
   * Omit it to inherit today's rendering exactly. This atom wraps the bare RN
   * primitive and contributes no typography of its own, so an omitted variant
   * leaves `className` untouched.
   */
  textVariant?: TextVariant;
}

const TextInput = React.forwardRef<
  React.ComponentRef<typeof RNTextInput>,
  TextInputProps
>(function TextInput({ UNSAFE_style, textVariant, ...props }, ref) {
  return (
    <RNTextInput
      ref={ref}
      style={UNSAFE_style}
      {...props}
      // No variant → no className override is emitted at all, so the props
      // reaching the primitive are the pre-SE-6596 props object, not an equal
      // one. The no-op default is structural rather than incidental.
      {...(textVariant === undefined
        ? {}
        : { className: withTextVariant(textVariant, props.className) })}
    />
  );
});

TextInput.displayName = "TextInput";

export { TextInput };
